import {
  type JobReturnAmountsSource,
  type JobReturnAmountsYear,
} from '@/features/jobs/Job'
import { match } from '@/utils/pattern-match-util'

/**
 * A year's federal amount plus all its state amounts.
 */
export function netAmount(year: JobReturnAmountsYear) {
  return (
    (year.federalAmount ?? 0) +
    year.stateAmounts.reduce((sum, s) => sum + s.amount, 0)
  )
}

/**
 * Where each of a year's amounts came from, federal first.
 */
function yearSources(year: JobReturnAmountsYear): JobReturnAmountsSource[] {
  return [
    ...(year.federalAmount !== undefined ? [year.federalSource] : []),
    ...year.stateAmounts.map((s) => s.source),
  ]
}

/**
 * Whether any of a year's amounts was typed in rather than extracted.
 */
export function hasManualAmount(year: JobReturnAmountsYear) {
  return yearSources(year).includes('Manual')
}

/**
 * Labels where a whole year's amounts came from, for views that show one
 * source per year; amounts are sourced one by one, so a year can be mixed.
 */
export function formatYearReturnAmountsSource(
  year: JobReturnAmountsYear,
): string | null {
  const sources = new Set(
    yearSources(year).filter((source) => source !== 'Unspecified'),
  )
  if (sources.size > 1) {
    return 'Mixed'
  }

  const [source] = sources
  return source ? formatReturnAmountsSource(source) : null
}

/**
 * Labels where an amount came from; amounts of unknown origin get none.
 */
export function formatReturnAmountsSource(
  source: JobReturnAmountsSource,
): string | null {
  return match(source, {
    Extracted: 'Auto-extracted',
    Manual: 'Manually entered',
    Unspecified: null,
  })
}
